import React, { useState } from 'react';
import { Palette, Monitor, Sun, Moon, Save, Loader2, Pipette, RotateCcw } from 'lucide-react';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import ToggleSwitch from '../../components/Settings/ToggleSwitch';
import useStore from '../../store/useStore';

const PRESET_COLORS = [
  { name: 'Orange', value: '#F97316' },
  { name: 'Blue', value: '#3B82F6' },
  { name: 'Emerald', value: '#10B981' },
  { name: 'Purple', value: '#8B5CF6' },
  { name: 'Rose', value: '#F43F5E' },
  { name: 'Cyan', value: '#06B6D4' },
  { name: 'Amber', value: '#F59E0B' },
  { name: 'Indigo', value: '#6366F1' },
  { name: 'Teal', value: '#14B8A6' },
  { name: 'Pink', value: '#EC4899' },
  { name: 'Lime', value: '#84CC16' },
  { name: 'Red', value: '#EF4444' },
];

const PRESET_BG_COLORS = [
  { name: 'Default Dark', value: '#0A0A0A' },
  { name: 'Slate', value: '#0F172A' },
  { name: 'Navy', value: '#172554' },
  { name: 'Charcoal', value: '#1C1917' },
  { name: 'Deep Purple', value: '#1E1B4B' },
  { name: 'Midnight', value: '#0C0A1D' },
  { name: 'Forest', value: '#0A1F12' },
  { name: 'Crimson', value: '#1C0A0A' },
  { name: 'Steel', value: '#111827' },
  { name: 'Obsidian', value: '#0D0D0D' },
  { name: 'Deep Teal', value: '#042F2E' },
  { name: 'Warm Gray', value: '#1A1614' },
];

const isValidHex = (val) => /^#[0-9A-Fa-f]{6}$/.test(val);

const AppearanceSettings = () => {
  const { theme, setTheme, accentColor, setAccentColor, customBg, setCustomBg } = useStore();
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState(null);
  const [hexInput, setHexInput] = useState(accentColor);
  const [bgHexInput, setBgHexInput] = useState(customBg || '#0A0A0A');

  const [settings, setSettings] = useState({
    compactMode: false,
    showAnimations: true,
    sidebarAutoCollapse: false,
  });

  const handleThemeChange = (newTheme) => {
    setTheme(newTheme);
    setSuccessMsg('Theme updated.');
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  const handleAccentChange = (color) => {
    setAccentColor(color);
    setHexInput(color);
  };

  const handleHexInput = (e) => {
    let val = e.target.value;
    if (val.length === 6 && !val.startsWith('#')) val = '#' + val;
    setHexInput(val);
    if (isValidHex(val)) {
      setAccentColor(val);
    }
  };

  const handleHexBlur = () => {
    if (!isValidHex(hexInput)) {
      setHexInput(accentColor);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    await new Promise((r) => setTimeout(r, 800));
    setSaving(false);
    setSuccessMsg('Appearance settings saved.');
    setTimeout(() => setSuccessMsg(null), 3000);
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h2 className="text-lg font-semibold text-white">Appearance</h2>
        <p className="text-sm text-gray-500 mt-1">
          Customize the look and feel of your dashboard.
        </p>
      </div>

      {successMsg && (
        <div
          role="status"
          aria-live="polite"
          className="px-4 py-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-sm text-emerald-400"
        >
          {successMsg}
        </div>
      )}

      {/* Theme Selection */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Palette className="w-5 h-5 text-orange-500/70" />
            <h3 className="text-base font-semibold text-gray-100">Theme</h3>
          </div>
        </Card.Header>
        <Card.Body>
          <div className="grid grid-cols-3 gap-4">
            {/* Dark Theme */}
            <button
              onClick={() => handleThemeChange('dark')}
              className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                theme === 'dark'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="w-12 h-12 rounded-lg bg-dark-900 border border-gray-700 flex items-center justify-center">
                <Moon className="w-6 h-6 text-orange-400" />
              </div>
              <span className="text-sm font-medium text-gray-200">Dark</span>
            </button>

            {/* Light Theme */}
            <button
              onClick={() => handleThemeChange('light')}
              className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                theme === 'light'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-300 flex items-center justify-center">
                <Sun className="w-6 h-6 text-amber-500" />
              </div>
              <span className="text-sm font-medium text-gray-200">Light</span>
            </button>

            {/* System Theme */}
            <button
              onClick={() => handleThemeChange('system')}
              className={`flex flex-col items-center gap-3 p-4 rounded-xl border-2 transition-all ${
                theme === 'system'
                  ? 'border-orange-500 bg-orange-500/10'
                  : 'border-gray-700 hover:border-gray-600'
              }`}
            >
              <div className="w-12 h-12 rounded-lg bg-dark-700 border border-gray-600 flex items-center justify-center">
                <Monitor className="w-6 h-6 text-gray-300" />
              </div>
              <span className="text-sm font-medium text-gray-200">System</span>
            </button>
          </div>
        </Card.Body>
      </Card>

      {/* Theme Background Color */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Palette className="w-5 h-5 text-orange-500/70" />
              <h3 className="text-base font-semibold text-gray-100">Theme Color</h3>
            </div>
            {customBg && (
              <button
                onClick={() => {
                  setCustomBg(null);
                  setBgHexInput('#0A0A0A');
                  setSuccessMsg('Theme color reset to default.');
                  setTimeout(() => setSuccessMsg(null), 3000);
                }}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-300 transition-colors"
              >
                <RotateCcw className="w-3 h-3" />
                Reset
              </button>
            )}
          </div>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="color"
                value={customBg || '#0A0A0A'}
                onChange={(e) => {
                  setCustomBg(e.target.value);
                  setBgHexInput(e.target.value);
                }}
                className="w-12 h-12 rounded-lg border-2 border-gray-700 cursor-pointer appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch]:rounded-md"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="bg-hex-input" className="block text-sm font-medium text-gray-200 mb-1">
                Background Color
              </label>
              <input
                id="bg-hex-input"
                type="text"
                value={bgHexInput}
                onChange={(e) => {
                  let val = e.target.value;
                  if (val.length === 6 && !val.startsWith('#')) val = '#' + val;
                  setBgHexInput(val);
                  if (isValidHex(val)) setCustomBg(val);
                }}
                onBlur={() => {
                  if (!isValidHex(bgHexInput)) {
                    setBgHexInput(customBg || '#0A0A0A');
                  }
                }}
                placeholder="#0A0A0A"
                maxLength={7}
                className={`w-full max-w-[140px] rounded-lg border bg-dark-900 px-3 py-2 text-sm text-white font-mono placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isValidHex(bgHexInput) ? 'border-gray-700' : 'border-red-500'
                }`}
              />
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2">Preset Backgrounds</p>
            <div className="grid grid-cols-6 gap-2">
              {PRESET_BG_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => {
                    setCustomBg(preset.value);
                    setBgHexInput(preset.value);
                  }}
                  title={preset.name}
                  className={`w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${
                    (customBg || '#0A0A0A') === preset.value
                      ? 'border-white scale-110'
                      : 'border-gray-700 hover:border-gray-500'
                  }`}
                  style={{ backgroundColor: preset.value }}
                />
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Accent Color */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Pipette className="w-5 h-5 text-orange-500/70" />
            <h3 className="text-base font-semibold text-gray-100">Accent Color</h3>
          </div>
        </Card.Header>
        <Card.Body className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="relative">
              <input
                type="color"
                value={accentColor}
                onChange={(e) => handleAccentChange(e.target.value)}
                className="w-12 h-12 rounded-lg border-2 border-gray-700 cursor-pointer appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:border-none [&::-webkit-color-swatch]:rounded-md"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="hex-input" className="block text-sm font-medium text-gray-200 mb-1">
                Hex Color Code
              </label>
              <input
                id="hex-input"
                type="text"
                value={hexInput}
                onChange={handleHexInput}
                onBlur={handleHexBlur}
                placeholder="#F97316"
                maxLength={7}
                className={`w-full max-w-[140px] rounded-lg border bg-dark-900 px-3 py-2 text-sm text-white font-mono placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-orange-500 ${
                  isValidHex(hexInput) ? 'border-gray-700' : 'border-red-500'
                }`}
              />
            </div>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-2">Preset Colors</p>
            <div className="grid grid-cols-6 gap-2">
              {PRESET_COLORS.map((preset) => (
                <button
                  key={preset.value}
                  onClick={() => handleAccentChange(preset.value)}
                  title={preset.name}
                  className={`w-9 h-9 rounded-lg border-2 transition-all hover:scale-110 ${
                    accentColor === preset.value
                      ? 'border-white scale-110'
                      : 'border-transparent hover:border-gray-600'
                  }`}
                  style={{ backgroundColor: preset.value }}
                />
              ))}
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Display Options */}
      <Card>
        <Card.Header>
          <h3 className="text-base font-semibold text-gray-100">Display Options</h3>
        </Card.Header>
        <Card.Body className="space-y-1">
          <ToggleSwitch
            label="Compact Mode"
            description="Reduce spacing and padding for a denser layout."
            enabled={settings.compactMode}
            onChange={(val) => setSettings((s) => ({ ...s, compactMode: val }))}
          />
          <ToggleSwitch
            label="Show Animations"
            description="Enable transitions and animations throughout the interface."
            enabled={settings.showAnimations}
            onChange={(val) => setSettings((s) => ({ ...s, showAnimations: val }))}
          />
          <ToggleSwitch
            label="Auto-Collapse Sidebar"
            description="Automatically collapse the sidebar when navigating."
            enabled={settings.sidebarAutoCollapse}
            onChange={(val) => setSettings((s) => ({ ...s, sidebarAutoCollapse: val }))}
          />
        </Card.Body>
      </Card>

      <div className="flex justify-end">
        <Button variant="primary" size="md" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Preferences
            </>
          )}
        </Button>
      </div>
    </div>
  );
};

export default AppearanceSettings;
